import type { VariantProps } from 'class-variance-authority';
import { ChevronDownIcon } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { badgeVariants } from '@/common/components/ui/badge';
import { Button } from '@/common/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/common/components/ui/dropdown-menu';

export interface MultiSelectOption<T extends string = string> {
  value: T;
  label: string;
  variant?: VariantProps<typeof badgeVariants>['variant'];
}

interface MultiSelectDropdownProps<T extends string = string> {
  value: T[];
  onChange: (values: T[]) => void;
  options: MultiSelectOption<T>[];
  placeholder?: string;
  renderMenuItem?: (option: MultiSelectOption<T>) => ReactNode;
  className?: string;
  contentWidth?: string;
}

export const MultiSelectDropdown = <T extends string = string>({
  value,
  onChange,
  options,
  placeholder,
  renderMenuItem,
  className,
  contentWidth = 'w-fit',
}: MultiSelectDropdownProps<T>) => {
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState<T[]>(value);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      const valuesChanged =
        selectedValues.length !== value.length ||
        selectedValues.some((val) => !value.includes(val));

      if (valuesChanged) {
        onChange(selectedValues);
      }
    } else {
      setSelectedValues(value);
    }
  };

  const handleToggle = (optionValue: T, checked: boolean) => {
    setSelectedValues((prev) =>
      checked ? [...prev, optionValue] : prev.filter((v) => v !== optionValue),
    );
  };

  const defaultRenderMenuItem = (option: MultiSelectOption<T>) => (
    <span className="text-sm">{option.label}</span>
  );

  const menuRenderer = renderMenuItem || defaultRenderMenuItem;

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`w-fit justify-between ${className || ''} hover:bg-white`}
        >
          <div className="flex gap-1 flex-wrap">
            {selectedValues.length > 0 ? (
              selectedValues.map((val) => {
                const option = options.find((opt) => opt.value === val);
                return option ? (
                  <span key={val} className="mx-1">
                    {menuRenderer(option)}
                  </span>
                ) : null;
              })
            ) : (
              <span className="text-muted-foreground">
                {placeholder ? placeholder : t('common.selectPlaceholder')}
              </span>
            )}
          </div>
          <ChevronDownIcon className="ml-2 h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={contentWidth}>
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selectedValues.includes(option.value)}
            onCheckedChange={(checked) => handleToggle(option.value, checked)}
            onSelect={(e) => {
              // Prevent dropdown closing when selecting a value
              e.preventDefault();
            }}
          >
            {menuRenderer(option)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
